import React, { useMemo } from 'react'
import { NumericFormat } from 'react-number-format'
import { TextField, FormControl, Tooltip, Icon, Typography } from '@mui/material'
import { withStyles } from '@mui/styles'
import { InputAdornment } from '@mui/material'

import { getEndAdornment } from './number-input.utils'
import { numberInputStyles } from './number-input.styles'

/**
 * Componente interno que formatea la entrada numérica usando react-number-format.
 * Aplica formato con separadores de miles y decimales, y valida el valor máximo.
 * 
 * @private
 */
function NumberFormatCustom(props) {
  const { inputRef, onChange, maxValue = 0, ...other } = props

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        })
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      allowNegative={false}
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values
        return formattedValue === '' || !maxValue || floatValue <= maxValue
      }}
    />
  )
}

/**
 * @typedef {object} NumberInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra el valor como texto estático en lugar de un campo editable.
 * @property {string | number} [value=''] - El valor actual del campo numérico.
 * @property {number} [decimalScale] - El número máximo de decimales permitidos.
 * @property {boolean} [fixedDecimalScale=false] - Si es `true`, siempre muestra el número especificado de decimales.
 * @property {number} [maxValue=0] - El valor máximo permitido. Un valor de `0` significa sin límite.
 * @property {string} [units] - Las unidades a mostrar después del número (ej. 'kg', 'm²', '€').
 * @property {string} [title] - El título que se muestra sobre el campo.
 * @property {string} [tooltip] - Un texto de ayuda que se muestra en un tooltip junto al título.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {string} name - El nombre del campo.
 * @property {function(React.ChangeEvent): void} onChange - La función callback que se ejecuta cuando el valor cambia.
 * @property {object} [InputProps] - Props adicionales a pasar al componente `TextField`.
 */

/**
 * Un componente de entrada numérica con formato automático y validación.
 * 
 * Utiliza `react-number-format` para proporcionar formato de números con separadores de miles (.)
 * y decimales (,) según el formato español. Soporta validación de valor máximo, decimales
 * configurables y unidades opcionales. Incluye un modo de solo lectura para visualización.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [price, setPrice] = useState('1500.50');
 * 
 *   const handleChange = (event) => {
 *     setPrice(event.target.value);
 *   };
 * 
 *   return (
 *     <NumberInput
 *       name="price"
 *       title="Precio"
 *       tooltip="Ingrese el precio del producto"
 *       value={price}
 *       onChange={handleChange}
 *       decimalScale={2}
 *       maxValue={10000}
 *       units="€"
 *     />
 *   );
 * };
 * 
 * @example
 * // Modo solo lectura
 * <NumberInput
 *   title="Total"
 *   value={2500}
 *   units="kg"
 *   readOnly
 * />
 * 
 * @param {NumberInputProps} props - Las props del componente NumberInput.
 */
export const NumberInput = withStyles(numberInputStyles)(({
  classes,
  readOnly = false,
  value = '',
  decimalScale = undefined,
  fixedDecimalScale = false,
  maxValue = 0,
  units = null,
  ...props
}) => {
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const InputProps = useMemo(() => {
    return {
      className: classes.input,
      ...props.InputProps,
      inputComponent: (inputComponentProps) => (
        <NumberFormatCustom {...{ decimalScale, maxValue, ...inputComponentProps }} />
      ),
      endAdornment: units ? (
        <InputAdornment position="end">{getEndAdornment(units)}</InputAdornment>
      ) : null,
    }
  }, [classes.input, props.InputProps, decimalScale, maxValue, units])

  const tooltip = useMemo(() => {
    return props.tooltip ? (
      <span style={{ fontSize: 16, marginLeft: 4 }}>
        <Tooltip
          placement="top"
          title={
            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              {props.tooltip}
            </Typography>
          }
        >
          <Icon fontSize="inherit">help_outline</Icon>
        </Tooltip>
      </span>
    ) : null
  }, [props.tooltip])

  return (
    <FormControl className={classes.root}>
      {props.title ? (
        <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
          {props.title + (tooltip ? tooltip : '')}
        </Typography>
      ) : null}
      {readOnly ? (
        <Typography
          variant={`body${typographyVariant}`}
          color="textSecondary"
          style={{ whiteSpace: 'pre-line' }}
        >
          {value || value === 0
            ? value.toString().replace(/\./g, ',') + (units ? ' ' + units : '')
            : '--'}
        </Typography>
      ) : (
        <TextField
          {...props}
          variant="outlined"
          value={value}
          onChange={props.onChange}
          name={props.name}
          InputProps={InputProps}
        />
      )}
    </FormControl>
  )
})
